import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { canPromote, importBadge } from '../lib/intentView';
import { hubClient, type ImportPreview, type IntentImportRow } from '../services/hubClient';

const SYSTEM = 'chatbot';

const TONE: Record<'gray' | 'amber' | 'green' | 'red', string> = {
  gray: 'bg-gray-100 text-gray-700',
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-700',
};

const PREVIEW_LABELS: [keyof ImportPreview, string][] = [
  ['intents', 'Intents to import'],
  ['general', 'General (platform defaults)'],
  ['bot', 'Bot-specific'],
  ['skippedControl', 'Skipped: commands'],
  ['skippedNoPhrases', 'Skipped: no phrases'],
  ['weak', 'Weak (1–2 phrases)'],
  ['phrases', 'Phrases'],
];

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Platform → Intent import (NLU P1 E10, plan B1). Import waves are run by the platform team: preview a v1 bot,
 * import it (or a list), then promote shadow → live. The server allows only services and SSO global admins.
 */
export function IntentImport() {
  const [rows, setRows] = useState<IntentImportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [botId, setBotId] = useState('');
  const [preview, setPreview] = useState<{ botId: string; data: ImportPreview } | null>(null);
  const [bulk, setBulk] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await hubClient.intentImport.list());
    } catch (e) {
      setError(errText(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while any import runs.
  useEffect(() => {
    if (!rows.some((r) => r.status === 'running')) return;
    const t = setTimeout(() => void load(), 3000);
    return () => clearTimeout(t);
  }, [rows, load]);

  async function act(fn: () => Promise<unknown>, ok?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  const id = botId.trim();
  const bulkIds = bulk.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold">Intent import</h1>
        <p className="text-sm text-gray-600">
          Copy a chatbot (v1) bot's intents into hub. Imported bots start in <b>shadow</b> (hub only logs); switch to{' '}
          <b>live</b> when ready. Edits in chatbot keep flowing in, and a nightly re-sync repairs anything missed.
        </p>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {notice && <div className="text-sm text-green-700">{notice}</div>}

      <div className="grid gap-4 md:grid-cols-2">
        <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <h2 className="text-sm font-medium text-gray-800">One bot</h2>
          <div className="flex gap-2">
            <input
              value={botId}
              onChange={(e) => {
                setBotId(e.target.value);
                setPreview(null);
              }}
              placeholder="v1 bot id, e.g. 7598"
              className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              disabled={busy || !id}
              onClick={() =>
                act(async () => setPreview({ botId: id, data: await hubClient.intentImport.preview(SYSTEM, id) }))
              }
              className="px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Preview
            </button>
            <button
              disabled={busy || !preview || preview.botId !== id}
              onClick={() => act(() => hubClient.intentImport.start(SYSTEM, id), `Import started for ${id}`)}
              className="px-3 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
            >
              Import
            </button>
          </div>
          {preview && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {PREVIEW_LABELS.map(([k, label]) => (
                <div key={k} className="contents">
                  <dt className="text-gray-500">{label}</dt>
                  <dd className="text-gray-800">{String(preview.data[k])}</dd>
                </div>
              ))}
              <dt className="text-gray-500">Duplicate names</dt>
              <dd className="text-gray-800">{preview.data.duplicateNames.join(', ') || '—'}</dd>
            </dl>
          )}
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <h2 className="text-sm font-medium text-gray-800">Many bots</h2>
          <textarea
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            placeholder="Bot ids separated by commas, spaces or new lines"
            rows={3}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            disabled={busy || bulkIds.length === 0}
            onClick={() =>
              act(async () => {
                const { queued } = await hubClient.intentImport.startBulk(SYSTEM, bulkIds);
                setNotice(`Queued ${queued} bot(s); two run at a time.`);
              })
            }
            className="px-3 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
          >
            Import all ({bulkIds.length})
          </button>
        </section>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-gray-800">Imported bots</h2>
        {rows.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">No bots imported yet.</div>
        ) : (
          <table className="w-full text-sm bg-white border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium">Bot</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">Progress</th>
                <th className="px-3 py-2 font-medium">Failed</th>
                <th className="px-3 py-2 font-medium">Last sync</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = importBadge(r);
                const canLive = canPromote(r);
                return (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <Link to={`/bots/${r.system}/${r.botId}/intents`} className="text-blue-600 hover:underline">
                        {r.botName ?? r.botId}
                      </Link>{' '}
                      <span className="text-xs text-gray-400">({r.botId})</span>
                    </td>
                    <td className="px-3 py-2">
                      {badge && <span className={`text-xs px-2 py-0.5 rounded ${TONE[badge.tone]}`}>{badge.label}</span>}
                      {r.error && <div className="text-xs text-red-600 mt-1">{r.error}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {r.processed}/{r.total}
                    </td>
                    <td className={`px-3 py-2 ${r.failed > 0 ? 'text-red-600' : 'text-gray-700'}`}>{r.failed}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {r.lastSyncedAt ? new Date(r.lastSyncedAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2 text-xs">
                        <button
                          disabled={busy || r.status === 'running'}
                          onClick={() => act(() => hubClient.intentImport.start(r.system, r.botId), `Re-sync started for ${r.botId}`)}
                          className="text-blue-600 hover:underline disabled:opacity-40"
                        >
                          Re-sync
                        </button>
                        {r.state === 'live' ? (
                          <button
                            disabled={busy}
                            onClick={() => act(() => hubClient.intentImport.setState(r.system, r.botId, 'shadow'))}
                            className="text-amber-700 hover:underline disabled:opacity-40"
                          >
                            Shadow
                          </button>
                        ) : (
                          <button
                            disabled={busy || !canLive}
                            title={canLive ? undefined : 'Needs a finished import with no failures'}
                            onClick={() => act(() => hubClient.intentImport.setState(r.system, r.botId, 'live'))}
                            className="text-green-700 hover:underline disabled:opacity-40"
                          >
                            Live
                          </button>
                        )}
                        <button
                          disabled={busy || r.status === 'running'}
                          onClick={() => {
                            if (confirm(`Remove ${r.botName ?? r.botId} from hub? Its synced phrases are unindexed.`)) {
                              void act(() => hubClient.intentImport.remove(r.system, r.botId), `Removed ${r.botId}`);
                            }
                          }}
                          className="text-red-600 hover:underline disabled:opacity-40"
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
